import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Check, Copy, Eye, Link2, Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import { subscribeProjectFiles, subscribeProjectFileStreams } from '../../utils/projectFileService';
import type { ProjectFile, ProjectFileStream, TimberProject, TradeBriefingLink, TradeBriefingTradeType } from '../../types';
import { defaultShowTimberList } from '../../utils/tradeBriefingScope';
import {
  buildTradeBriefingFileGroups,
  selectionsFromTradeBriefingFileIds,
  type TradeBriefingFileGroupKind,
} from '../../utils/tradeBriefingFiles';
import {
  buildTradeCompanyOptions,
  findDealLinkForCompany,
  tradeCompanyIdentityKey,
  type TradeCompanyOption,
} from '../../utils/tradeBriefingCompanies';
import { previewFromCompany, tradePortalPreviewHref } from '../../utils/tradePortalPreview';
import { canPreviewPartnerPortals } from '../../utils/userRoles';
import './TradeBriefingLinksPanel.css';

const TRADE_OPTIONS: Array<{ value: TradeBriefingTradeType; label: string }> = [
  { value: 'finishing', label: 'Finishing' },
  { value: 'millwork', label: 'Millwork' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_CONFIDENTIAL_BANNER =
  'CONFIDENTIAL — This briefing is for authorized subcontractors only. Do not share, copy, or distribute project documents.';

type LinkRow = TradeBriefingLink & { id: string };

export type TradeBriefingLinksContentProps = {
  dealId: string;
  timberProjectId: string;
};

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function defaultShowStructureScope(trade: TradeBriefingTradeType): boolean {
  return trade !== 'electrical' && trade !== 'roofing';
}

function tradeTypeLabel(trade: string | undefined): string {
  return TRADE_OPTIONS.find(opt => opt.value === trade)?.label || trade || 'General';
}

function inviteStatusLabel(link: LinkRow): string {
  if (link.revokedAt) return 'Revoked';
  if (!link.inviteEmail) return 'Legacy link';
  // "accepted" means the contact finished the invite (or was an existing
  // subcontractor) and their account is linked to this briefing.
  return link.inviteStatus === 'accepted' ? 'Account linked' : 'Invite sent';
}

function otherProjectHint(company: TradeCompanyOption, dealId: string): string | null {
  const others = company.projectCount - (company.dealIds.includes(dealId) ? 1 : 0);
  if (others <= 0) return null;
  return others === 1 ? 'Also on 1 other project' : `Also on ${others} other projects`;
}

function fileKindBadge(kind: TradeBriefingFileGroupKind): { label: string; className: string } {
  if (kind === 'three_d_html') {
    return { label: '3D', className: 'tb-links-file-badge tb-links-file-badge--3d' };
  }
  return { label: 'PDF', className: 'tb-links-file-badge tb-links-file-badge--pdf' };
}

const TradeBriefingLinksContent: React.FC<TradeBriefingLinksContentProps> = ({
  dealId,
  timberProjectId,
}) => {
  const { user } = useAuth();
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [projectStreams, setProjectStreams] = useState<ProjectFileStream[]>([]);
  const [busy, setBusy] = useState(false);
  const [inviteSendingId, setInviteSendingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);

  const [allLinks, setAllLinks] = useState<LinkRow[]>([]);
  const [companyMode, setCompanyMode] = useState<'existing' | 'new'>('new');
  const [companyKey, setCompanyKey] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [tradeType, setTradeType] = useState<TradeBriefingTradeType>('general');
  const [displayName, setDisplayName] = useState('');
  const [accessPin, setAccessPin] = useState('');
  const [briefingNotes, setBriefingNotes] = useState('');
  const [showStructureScope, setShowStructureScope] = useState(true);
  const [showTimberList, setShowTimberList] = useState(true);
  const [includeQuoteUpload, setIncludeQuoteUpload] = useState(false);
  const [groupSelections, setGroupSelections] = useState<Record<string, string>>({});
  const [confidentialBanner, setConfidentialBanner] = useState('');
  const [savedConfidentialBanner, setSavedConfidentialBanner] = useState('');
  const [savingBanner, setSavingBanner] = useState(false);
  const [bannerSaved, setBannerSaved] = useState(false);

  const pendingFileIdsRef = useRef<string[] | null>(null);
  const companyInputRef = useRef<HTMLInputElement | null>(null);

  const bannerEnabled = Boolean(savedConfidentialBanner.trim());
  const bannerDirty = confidentialBanner.trim() !== savedConfidentialBanner.trim();

  const fileGroups = useMemo(
    () => buildTradeBriefingFileGroups(projectStreams, projectFiles),
    [projectStreams, projectFiles],
  );

  useEffect(() => {
    if (!timberProjectId) return;
    const unsub = onSnapshot(
      doc(db, 'timberProjects', timberProjectId),
      snap => {
        const value =
          typeof (snap.data() as TimberProject | undefined)?.tradeBriefingConfidentialBanner === 'string'
            ? (snap.data() as TimberProject).tradeBriefingConfidentialBanner!.trim()
            : '';
        setConfidentialBanner(value);
        setSavedConfidentialBanner(value);
      },
      err => console.error('[TradeBriefingLinks] project banner', err),
    );
    return () => unsub();
  }, [timberProjectId]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'tradeBriefingLinks'),
      snap => {
        setAllLinks(snap.docs.map(d => ({ id: d.id, ...(d.data() as TradeBriefingLink) })));
      },
      err => console.error('[TradeBriefingLinks]', err),
    );
    return () => unsub();
  }, []);

  const companies = useMemo(() => buildTradeCompanyOptions(allLinks), [allLinks]);


  const links = useMemo(
    () =>
      allLinks
        .filter(link => link.dealId === dealId)
        .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)),
    [allLinks, dealId],
  );

  const selectedCompany = useMemo(
    () => companies.find(row => row.key === companyKey) || null,
    [companies, companyKey],
  );

  useEffect(() => {
    if (!timberProjectId) {
      setProjectFiles([]);
      setProjectStreams([]);
      return;
    }
    const unsubFiles = subscribeProjectFiles(timberProjectId, setProjectFiles);
    const unsubStreams = subscribeProjectFileStreams(timberProjectId, setProjectStreams);
    return () => {
      unsubFiles();
      unsubStreams();
    };
  }, [timberProjectId]);

  useEffect(() => {
    if (!fileGroups.length) return;
    setGroupSelections(prev => {
      const pending = pendingFileIdsRef.current;
      const ids = pending ?? Object.values(prev).filter(Boolean);
      if (pending) pendingFileIdsRef.current = null;
      if (!ids.length && !pending) return prev;
      return selectionsFromTradeBriefingFileIds(fileGroups, ids);
    });
  }, [fileGroups]);

  const activeLinks = useMemo(
    () => links.filter(link => !link.revokedAt),
    [links],
  );

  const briefingUrl = (token: string) => `${window.location.origin}/briefing/${token}`;

  const applyCompany = (company: TradeCompanyOption, resetScope = false) => {
    setCompanyMode('existing');
    setCompanyKey(company.key);
    setCompanyName(company.companyName);
    setContactName(company.contactName);
    setInviteEmail(company.inviteEmail);
    setTradeType(company.tradeType);
    if (resetScope) {
      setShowStructureScope(defaultShowStructureScope(company.tradeType));
      setShowTimberList(defaultShowTimberList(company.tradeType));
    }
  };

  const resetForm = () => {
    setEditingLinkId(null);
    setCompanyMode('new');
    setCompanyKey('');
    setCompanyName('');
    setContactName('');
    setInviteEmail('');
    setTradeType('general');
    setDisplayName('');
    setAccessPin('');
    setBriefingNotes('');
    setShowStructureScope(defaultShowStructureScope('general'));
    setShowTimberList(defaultShowTimberList('general'));
    setIncludeQuoteUpload(false);
    setGroupSelections({});
    pendingFileIdsRef.current = null;
  };

  const handleNewTrade = () => {
    const dirtyBlank =
      !editingLinkId &&
      Boolean(
        companyName.trim() ||
          contactName.trim() ||
          inviteEmail.trim() ||
          displayName.trim() ||
          accessPin.trim() ||
          briefingNotes.trim(),
      );
    if (dirtyBlank && !window.confirm('Start over? The form will be cleared.')) return;
    resetForm();
    queueMicrotask(() => companyInputRef.current?.focus());
  };

  const handleSaveConfidentialBanner = async (nextValue?: string) => {
    if (!timberProjectId) return;
    const trimmed = (nextValue ?? confidentialBanner).trim();
    setSavingBanner(true);
    setBannerSaved(false);
    try {
      await updateDoc(doc(db, 'timberProjects', timberProjectId), {
        tradeBriefingConfidentialBanner: trimmed || null,
        updatedAt: Timestamp.now(),
      });
      setConfidentialBanner(trimmed);
      setSavedConfidentialBanner(trimmed);
      setBannerSaved(true);
      setTimeout(() => setBannerSaved(false), 2000);
    } catch (e) {
      console.error('Save trade briefing banner:', e);
      alert('Could not save project banner');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleToggleConfidentialBanner = async (enabled: boolean) => {
    if (savingBanner) return;
    if (enabled) {
      const text = confidentialBanner.trim() || DEFAULT_CONFIDENTIAL_BANNER;
      setConfidentialBanner(text);
      await handleSaveConfidentialBanner(text);
      return;
    }
    setConfidentialBanner('');
    await handleSaveConfidentialBanner('');
  };

  const includedProjectFileIds = useMemo(
    () => Object.values(groupSelections).filter(Boolean),
    [groupSelections],
  );

  const handleCompanySelect = (value: string) => {
    if (value === '__new__') {
      const keepName = companyMode === 'new' ? companyName : '';
      resetForm();
      setCompanyName(keepName);
      queueMicrotask(() => companyInputRef.current?.focus());
      return;
    }
    const company = companies.find(row => row.key === value);
    if (!company) return;
    const existing = findDealLinkForCompany(links, company.key);
    if (existing) {
      handleStartEdit(existing);
      return;
    }
    if (editingLinkId) {
      applyCompany(company);
      return;
    }
    resetForm();
    applyCompany(company, true);
  };

  const linkPayload = () => ({
    companyName: companyName.trim(),
    contactName: contactName.trim() || null,
    inviteEmail: inviteEmail.trim().toLowerCase() || null,
    invitedUid: (() => {
      const email = inviteEmail.trim().toLowerCase();
      if (selectedCompany?.invitedUid && selectedCompany.inviteEmail === email) {
        return selectedCompany.invitedUid;
      }
      const current = editingLinkId ? links.find(row => row.id === editingLinkId) : undefined;
      if (current?.invitedUid && (current.inviteEmail || '').trim().toLowerCase() === email) {
        return current.invitedUid;
      }
      return null;
    })(),
    tradeType,
    displayName: displayName.trim() || null,
    accessPin: accessPin.trim() || null,
    briefingNotes: briefingNotes.trim() || null,
    includedProjectFileIds,
    showStructureScope,
    showTimberList: showStructureScope && showTimberList,
    includeQuoteUpload,
  });

  const sendInvite = async (linkId: string, email?: string) => {
    if (inviteSendingId) return;
    setInviteSendingId(linkId);
    const fn = httpsCallable<
      { linkId: string; email?: string; name?: string },
      {
        ok?: boolean;
        reused?: boolean;
        method?: string;
        setupLink?: string;
        inviteUrl?: string;
        alreadySent?: boolean;
      }
    >(getFunctions(undefined, 'us-central1'), 'sendTradeBriefingInvite');
    try {
      const { data } = await fn({
        linkId,
        email: (email || inviteEmail).trim().toLowerCase() || undefined,
        name: contactName.trim() || companyName.trim() || undefined,
      });
      if (data.alreadySent) {
        alert('An invite was already sent to this email recently. Wait a minute before sending again.');
        return;
      }
      if (data.setupLink && data.inviteUrl && data.method !== 'email') {
        alert(
          `Invite created. Share this invite URL and setup link:\n${data.inviteUrl}\n${data.setupLink}`,
        );
      }
    } catch (err) {
      console.error('Send trade briefing invite:', err);
      alert(err instanceof Error ? err.message : 'Could not send invite');
    } finally {
      setInviteSendingId(null);
    }
  };

  const handleCreateOrSave = async () => {
    if (!companyName.trim()) {
      alert('Company is required.');
      companyInputRef.current?.focus();
      return;
    }
    if (!user?.id) {
      alert('You need to be signed in to add a trade.');
      return;
    }
    if (!dealId) {
      alert('This project is not linked to a deal, so a trade cannot be added.');
      return;
    }
    if (accessPin && !/^\d{4}$/.test(accessPin)) {
      alert('PIN must be exactly 4 digits, or leave blank for link-only access.');
      return;
    }
    const payload = linkPayload();
    const identityKey = tradeCompanyIdentityKey({
      companyName: payload.companyName,
      inviteEmail: payload.inviteEmail,
      invitedUid: payload.invitedUid,
    });
    const duplicate = findDealLinkForCompany(
      links.filter(row => row.id !== editingLinkId),
      identityKey,
    );
    if (duplicate) {
      alert('This company is already on this project.');
      handleStartEdit(duplicate);
      return;
    }
    setBusy(true);
    try {
      if (editingLinkId) {
        const previous = links.find(row => row.id === editingLinkId);
        const nextEmail = inviteEmail.trim().toLowerCase();
        await updateDoc(doc(db, 'tradeBriefingLinks', editingLinkId), payload);
        if (nextEmail && nextEmail !== (previous?.inviteEmail || '').trim().toLowerCase()) {
          await sendInvite(editingLinkId, nextEmail);
        }
      } else {
        const linkRef = doc(collection(db, 'tradeBriefingLinks'));
        await setDoc(linkRef, {
          dealId,
          timberProjectId,
          ...payload,
          inviteStatus: payload.invitedUid ? 'accepted' : inviteEmail.trim() ? 'pending' : null,
          createdAt: Timestamp.now(),
          createdBy: user.id,
          revokedAt: null,
        });
        if (inviteEmail.trim()) {
          await sendInvite(linkRef.id, inviteEmail);
        }
        handleStartEdit({
          id: linkRef.id,
          dealId,
          timberProjectId,
          ...payload,
          inviteStatus: payload.invitedUid ? 'accepted' : inviteEmail.trim() ? 'pending' : undefined,
        } as LinkRow);
      }
    } catch (e) {
      console.error(editingLinkId ? 'Update trade briefing link:' : 'Create trade briefing link:', e);
      alert(editingLinkId ? 'Could not save briefing link' : 'Could not create briefing link');
    } finally {
      setBusy(false);
    }
  };

  const handleStartEdit = (link: LinkRow) => {
    setEditingLinkId(link.id);
    const key = tradeCompanyIdentityKey(link);
    const known = Boolean(key && companies.some(row => row.key === key));
    setCompanyMode(known ? 'existing' : 'new');
    setCompanyKey(known ? key : '');
    setCompanyName(link.companyName || '');
    setContactName(link.contactName || '');
    setInviteEmail(link.inviteEmail || '');
    const nextTrade = TRADE_OPTIONS.some(opt => opt.value === link.tradeType) ? link.tradeType : 'general';
    setTradeType(nextTrade);
    setDisplayName(link.displayName || '');
    setAccessPin(link.accessPin || '');
    setBriefingNotes(link.briefingNotes || '');
    setShowStructureScope(
      typeof link.showStructureScope === 'boolean'
        ? link.showStructureScope
        : defaultShowStructureScope(nextTrade),
    );
    setIncludeQuoteUpload(link.includeQuoteUpload === true);
    setShowTimberList(link.showTimberList !== false);
    const fileIds = link.includedProjectFileIds ?? [];
    if (fileGroups.length) {
      setGroupSelections(selectionsFromTradeBriefingFileIds(fileGroups, fileIds));
      pendingFileIdsRef.current = null;
    } else {
      pendingFileIdsRef.current = fileIds;
      setGroupSelections({});
    }
  };

  const handleDelete = async (link: LinkRow) => {
    if (!window.confirm('Delete this trade? They will lose access immediately.')) return;
    try {
      const inviteIds = new Set<string>();
      if (link.inviteId) inviteIds.add(link.inviteId);
      try {
        const invitesSnap = await getDocs(
          query(collection(db, 'tradeBriefingInvites'), where('linkId', '==', link.id)),
        );
        invitesSnap.forEach(inviteDoc => inviteIds.add(inviteDoc.id));
      } catch (inviteQueryErr) {
        console.warn('[TradeBriefingLinks] invite cleanup query', inviteQueryErr);
      }
      await Promise.all([
        ...Array.from(inviteIds).map(id => deleteDoc(doc(db, 'tradeBriefingInvites', id))),
        deleteDoc(doc(db, 'tradeBriefingLinks', link.id)),
      ]);
      if (editingLinkId === link.id) resetForm();
    } catch (e) {
      console.error('Delete trade briefing link:', e);
      alert('Could not delete trade');
    }
  };

  const handleCopy = (linkId: string, pin?: string) => {
    const url = briefingUrl(linkId);
    const text = pin ? `${url}\nAccess code: ${pin}` : url;
    void navigator.clipboard.writeText(text);
    setCopiedId(linkId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleTradeChange = (next: TradeBriefingTradeType) => {
    setTradeType(next);
    if (!editingLinkId) {
      setShowStructureScope(defaultShowStructureScope(next));
      setShowTimberList(defaultShowTimberList(next));
    }
  };

  const handleGroupSelect = (groupKey: string, fileId: string) => {
    setGroupSelections(prev => ({ ...prev, [groupKey]: fileId }));
  };

  const renderFileTable = () => {
    if (!fileGroups.length) {
      return <p className="tb-links-hint">No 3D HTML or addendum PDFs yet.</p>;
    }
    return (
      <div className="tb-links-file-table-wrap">
        <table className="tb-links-file-table">
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Type</th>
              <th scope="col">Include</th>
            </tr>
          </thead>
          <tbody>
            {fileGroups.map(group => {
              const badge = fileKindBadge(group.kind);
              const selectedId = groupSelections[group.key] ?? '';
              return (
                <tr key={group.key} className={selectedId ? 'is-included' : undefined}>
                  <td className="tb-links-file-name" title={group.title}>{group.title}</td>
                  <td>
                    <span className={badge.className}>{badge.label}</span>
                  </td>
                  <td>
                    <select
                      aria-label={`Include ${group.title}`}
                      value={selectedId}
                      onChange={e => handleGroupSelect(group.key, e.target.value)}
                    >
                      <option value="">Skip</option>
                      {group.versions.map(version => (
                        <option key={version.fileId} value={version.fileId}>
                          {version.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const selectedLink = editingLinkId ? links.find(row => row.id === editingLinkId) : undefined;
  const canPreview = canPreviewPartnerPortals(user?.role);
  const portalPreviewTarget = selectedCompany
    ? previewFromCompany(selectedCompany)
    : companyName.trim()
      ? previewFromCompany({
          key: tradeCompanyIdentityKey({
            companyName,
            inviteEmail,
            invitedUid: selectedLink?.invitedUid,
          }),
          invitedUid: selectedLink?.invitedUid || '',
          inviteEmail: inviteEmail.trim().toLowerCase(),
        })
      : null;

  return (
    <div className="tb-links-panel-body tb-links-panel-body--embedded">
      <p className="tb-links-intro">
        Share structure scope and Project Hub files with finishing, millwork, electrical, roofing, and other
        field subs — no pricing.
      </p>

      <section className="tb-links-project-banner">
        <label className="tb-links-banner-toggle">
          <input
            type="checkbox"
            checked={bannerEnabled}
            disabled={savingBanner}
            onChange={e => void handleToggleConfidentialBanner(e.target.checked)}
          />
          <span className="tb-links-banner-toggle-text">
            <strong>Show confidential banner</strong>
            <span>Appears at the top of every trade briefing for this project</span>
          </span>
        </label>

        {bannerEnabled ? (
          <div className="tb-links-banner-editor">
            <label className="tb-links-banner-field" htmlFor="tb-confidential-banner-text">
              Banner message
            </label>
            <textarea
              id="tb-confidential-banner-text"
              className="tb-links-banner-textarea"
              value={confidentialBanner}
              onChange={e => setConfidentialBanner(e.target.value)}
              rows={4}
            />
            <div className="tb-links-project-banner-actions">
              <button
                type="button"
                className="tb-links-banner-save-btn"
                onClick={() => void handleSaveConfidentialBanner()}
                disabled={savingBanner || !bannerDirty}
              >
                {savingBanner ? (
                  <Loader2 size={16} className="tb-links-spinner" />
                ) : bannerSaved ? (
                  <Check size={16} />
                ) : (
                  <Save size={16} />
                )}
                {savingBanner ? 'Saving…' : bannerSaved ? 'Saved' : 'Save message'}
              </button>
              {confidentialBanner.trim() !== DEFAULT_CONFIDENTIAL_BANNER ? (
                <button
                  type="button"
                  className="tb-links-banner-clear-btn"
                  onClick={() => {
                    setConfidentialBanner(DEFAULT_CONFIDENTIAL_BANNER);
                    void handleSaveConfidentialBanner(DEFAULT_CONFIDENTIAL_BANNER);
                  }}
                  disabled={savingBanner}
                >
                  Reset to default
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <div className="tb-links-workspace">
      <div className={`tb-links-form${editingLinkId ? ' tb-links-form--editing' : ''}`}>
        <div className="tb-links-form-head">
          <h4 className="tb-links-form-title">
            {selectedLink ? selectedLink.companyName || 'Trade details' : 'New trade'}
          </h4>
          {selectedLink ? (
            <span className={`tb-links-form-status${selectedLink.revokedAt ? ' is-revoked' : selectedLink.inviteStatus === 'accepted' ? ' is-accepted' : ''}`}>
              {inviteStatusLabel(selectedLink)}
            </span>
          ) : null}
        </div>
        <div className="tb-links-form-grid">
          <label className="tb-links-company-field">
            Company *
            <select
              aria-label="Select a trade company"
              value={companyMode === 'existing' && companyKey ? companyKey : '__new__'}
              onChange={e => handleCompanySelect(e.target.value)}
            >
              <option value="__new__">Add new company</option>
              {companies.map(company => (
                <option key={company.key} value={company.key}>
                  {company.companyName}
                  {company.projectCount > 1 ? ` · ${company.projectCount} projects` : ''}
                </option>
              ))}
            </select>
            {companyMode === 'new' ? (
              <input
                ref={companyInputRef}
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="ABC Finishing"
              />
            ) : selectedCompany && otherProjectHint(selectedCompany, dealId) ? (
              <span className="tb-links-company-hint">{otherProjectHint(selectedCompany, dealId)}</span>
            ) : null}
          </label>
          <label>
            Contact
            <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
          </label>
          <label>
            Email
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="Optional — to invite a login"
              title="Add an email to invite them to sign in. You can create the trade now and invite later."
            />
          </label>
          <label>
            Trade
            <select
              value={tradeType}
              onChange={e => handleTradeChange(e.target.value as TradeBriefingTradeType)}
            >
              {TRADE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Label
            <input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Millwork"
            />
          </label>
          <label>
            PIN
            <input
              value={accessPin}
              onChange={e => setAccessPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              placeholder="Optional"
              maxLength={4}
              title="Legacy links only. Leave blank when inviting by email."
            />
          </label>
        </div>
        <label>
          Notes
          <textarea
            value={briefingNotes}
            onChange={e => setBriefingNotes(e.target.value)}
            rows={2}
            placeholder="Site access, finish spec, coordination…"
          />
        </label>

        <div className="tb-links-checks">
          <label className="tb-links-checkbox" title="Species, decking, and install context">
            <input
              type="checkbox"
              checked={showStructureScope}
              onChange={e => setShowStructureScope(e.target.checked)}
            />
            Structure
          </label>
          <label
            className={`tb-links-checkbox${showStructureScope ? '' : ' tb-links-checkbox--disabled'}`}
            title="Piece sizes and lengths from the proposal. No pricing."
          >
            <input
              type="checkbox"
              checked={showStructureScope && showTimberList}
              disabled={!showStructureScope}
              onChange={e => setShowTimberList(e.target.checked)}
            />
            Timber list
          </label>
          <label className="tb-links-checkbox" title="Let this sub upload a bid">
            <input
              type="checkbox"
              checked={includeQuoteUpload}
              onChange={e => setIncludeQuoteUpload(e.target.checked)}
            />
            Quote
          </label>
        </div>

        <div className="tb-links-form-actions">
          <button
            type="button"
            className="tb-links-create-btn"
            onClick={() => void handleCreateOrSave()}
            disabled={busy || !companyName.trim()}
          >
            {busy ? <Loader2 size={14} className="tb-links-spinner" /> : editingLinkId ? <Save size={14} /> : <Link2 size={14} />}
            {editingLinkId ? 'Save' : 'Add trade'}
          </button>
          {selectedLink && !selectedLink.revokedAt ? (
            selectedLink.inviteEmail ? (
              <button
                type="button"
                className="tb-links-cancel-btn"
                onClick={() => void sendInvite(selectedLink.id, selectedLink.inviteEmail)}
                disabled={busy || inviteSendingId === selectedLink.id}
              >
                {inviteSendingId === selectedLink.id
                  ? 'Sending…'
                  : selectedLink.inviteStatus === 'accepted'
                    ? 'Resend'
                    : 'Invite'}
              </button>
            ) : (
              <button
                type="button"
                className="tb-links-cancel-btn"
                onClick={() => handleCopy(selectedLink.id, selectedLink.accessPin)}
              >
                {copiedId === selectedLink.id ? <Check size={14} /> : <Copy size={14} />}
                {copiedId === selectedLink.id ? 'Copied' : 'Copy'}
              </button>
            )
          ) : null}
          {selectedLink ? (
            <button
              type="button"
              className="tb-links-cancel-btn tb-links-delete-btn"
              onClick={() => void handleDelete(selectedLink)}
              disabled={busy}
            >
              <Trash2 size={14} />
              Delete
            </button>
          ) : null}
          {editingLinkId ? (
            <button type="button" className="tb-links-cancel-btn" onClick={handleNewTrade} disabled={busy}>
              <X size={14} />
              New
            </button>
          ) : null}
          {canPreview && selectedLink && !selectedLink.revokedAt ? (
            <a
              className="tb-links-cancel-btn"
              href={`/briefing/${selectedLink.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <Eye size={14} />
              Preview briefing
            </a>
          ) : null}
          {canPreview && portalPreviewTarget?.companyKey ? (
            <a
              className="tb-links-cancel-btn"
              href={tradePortalPreviewHref(portalPreviewTarget)}
              target="_blank"
              rel="noreferrer"
            >
              <Eye size={14} />
              Preview portal
            </a>
          ) : null}
        </div>

        <div className="tb-links-file-groups">
          <p className="tb-links-file-heading">Files</p>
          {editingLinkId &&
          links.find(row => row.id === editingLinkId)?.includedProjectFileIds === undefined ? (
            <p className="tb-links-hint">Saving switches this link to the hub files below.</p>
          ) : null}
          {renderFileTable()}
        </div>
      </div>

      <aside className="tb-links-roster" aria-label="Invited trades">
        <div className="tb-links-roster-head">
          <h4>Invited trades</h4>
          <span>{activeLinks.length}</span>
        </div>
        <button
          type="button"
          className={`tb-links-roster-new${!editingLinkId ? ' is-active' : ''}`}
          onClick={handleNewTrade}
          disabled={busy}
        >
          <Plus size={14} aria-hidden />
          New trade
        </button>
        {links.length === 0 ? (
          <p className="tb-links-roster-empty">No trades yet. Fill in the form and click Add trade.</p>
        ) : (
          <ul className="tb-links-list">
            {links.map(link => {
              const revoked = Boolean(link.revokedAt);
              const lastViewed = toDate(link.lastViewedAt);
              const isEditing = editingLinkId === link.id;
              return (
                <li key={link.id}>
                  <button
                    type="button"
                    className={`tb-links-roster-item${revoked ? ' is-revoked' : ''}${isEditing ? ' is-selected' : ''}`}
                    onClick={() => handleStartEdit(link)}
                    disabled={busy}
                  >
                    <span className="tb-links-roster-name">{link.companyName || 'Untitled trade'}</span>
                    <span className="tb-links-roster-meta">
                      <span className="tb-links-trade">{tradeTypeLabel(link.tradeType)}</span>
                      <span className={`tb-links-roster-status${revoked ? ' is-revoked' : link.inviteStatus === 'accepted' ? ' is-accepted' : ''}`}>
                        {inviteStatusLabel(link)}
                      </span>
                    </span>
                    {(() => {
                      const company = companies.find(row => row.key === tradeCompanyIdentityKey(link));
                      const others = company ? otherProjectHint(company, dealId) : null;
                      if (!link.displayName && !others) return null;
                      return (
                        <span className="tb-links-roster-sub">
                          {[link.displayName, others].filter(Boolean).join(' · ')}
                        </span>
                      );
                    })()}
                    <span className="tb-links-roster-sub">
                      {link.inviteEmail || (link.accessPin ? `PIN ${link.accessPin}` : 'No email')}
                      {' · '}
                      {lastViewed ? `Viewed ${lastViewed.toLocaleDateString()}` : 'Not viewed'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
      </div>
    </div>
  );
};

export default TradeBriefingLinksContent;
